import React, { useState, useEffect } from 'react';
import { SAPLayout } from '@/layouts/SAPLayout/SAPLayout';
import { mockSapSimulatorService } from '@/services/mock/MockSapSimulatorService';
import { SapInvoiceRecord } from '@/types/domain/sap-simulator';
import { DataTable, ColumnDef } from '@/components/data-display/DataTable';
import { formatCurrency } from '@/lib/utils';
import { Receipt } from 'lucide-react';

export const SapInvoicesPage: React.FC = () => {
  const [invoices, setInvoices] = useState<SapInvoiceRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadInvoices = async () => {
      try {
        setIsLoading(true);
        const data = await mockSapSimulatorService.getInvoices();
        setInvoices(data.data);
      } finally {
        setIsLoading(false);
      }
    };
    loadInvoices();
  }, []);

  const columns: ColumnDef<SapInvoiceRecord>[] = [
    {
      header: 'SAP Invoice Number',
      accessorKey: 'invoiceNumber',
      cell: (row) => <span className="font-mono font-extrabold text-purple-600 text-xs">{row.invoiceNumber}</span>,
    },
    {
      header: 'PO Reference',
      accessorKey: 'poNumber',
      cell: (row) => <span className="font-mono font-bold text-emerald-600 text-xs">{row.poNumber}</span>,
    },
    {
      header: 'Vendor Code',
      accessorKey: 'vendorCode',
      cell: (row) => <span className="font-mono text-xs text-foreground font-semibold">{row.vendorCode}</span>,
    },
    {
      header: 'Invoice Date & Due Date',
      cell: (row) => (
        <span className="text-xs text-foreground-secondary font-medium">
          {row.invoiceDate} (Due {row.dueDate})
        </span>
      ),
    },
    {
      header: 'Invoice Amount',
      accessorKey: 'invoiceAmount',
      cell: (row) => <span className="font-extrabold text-xs text-foreground">{formatCurrency(row.invoiceAmount, 'INR')}</span>,
    },
    {
      header: '3-Way Match Status',
      accessorKey: 'matchingStatus',
      cell: (row) => (
        <span
          className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${
            row.matchingStatus === 'MATCHED'
              ? 'bg-emerald-500/10 text-emerald-700'
              : 'bg-rose-500/10 text-rose-700'
          }`}
        >
          {row.matchingStatus}
        </span>
      ),
    },
    {
      header: 'Payment Status',
      accessorKey: 'paymentStatus',
      cell: (row) => (
        <span className="text-[10px] font-extrabold uppercase bg-muted text-foreground-secondary px-2 py-0.5 rounded">
          {row.paymentStatus}
        </span>
      ),
    },
  ];

  return (
    <SAPLayout>
      <div className="space-y-6 font-heading">
        <div>
          <h2 className="text-xl font-extrabold text-foreground flex items-center gap-2">
            <Receipt className="h-5 w-5 text-purple-600" /> SAP Invoice Verification (MIRO) & 3-Way Matching
          </h2>
          <p className="text-xs text-foreground-secondary font-medium">
            Simulated MIRO 3-way matching comparing Purchase Order value vs Goods Receipt value vs Vendor Invoice amount.
          </p>
        </div>

        <DataTable data={invoices} columns={columns} isLoading={isLoading} />
      </div>
    </SAPLayout>
  );
};
